/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable no-param-reassign */
import { DeviceType, LAYOUT_SECTION } from '@adalo/constants'
import { getId } from '@adalo/utils'

import ObjectBuilder from 'utils/ObjectBuilder'
import { EditorObject } from 'utils/responsiveTypes'
import traverseChildren from 'utils/objects/traverseChildren'

import { replaceAllOccurrencesFromMap } from './utils'
import { getContainerChildrenFromSection } from '..'

const devices = [DeviceType.DESKTOP, DeviceType.TABLET, DeviceType.MOBILE]

/**
 * Creates a new layout section from the data of the sidebar layout sections with preset children
 */
const createLayoutSectionFromPrebuilt = (
  object: EditorObject
): EditorObject => {
  const innerSectionChildren = getContainerChildrenFromSection(object)

  if (object.type !== LAYOUT_SECTION) {
    throw new Error(
      `Tried to create a new preset layout section from a non-layout section object`
    )
  }

  if (!innerSectionChildren || innerSectionChildren.length === 0) {
    throw new Error(
      `Tried to create a new layout section from a preset with no children`
    )
  }

  let newObject = { ...object }
  const { id: newObjectId } = newObject

  const newSharedX = newObject.x
  const newSharedY = newObject.y

  // newObject already has an updated id
  const idMap: Record<string, string> = {
    [newObjectId]: newObjectId,
  }

  // translate all children positions on all devices
  // replace ids & save on map to replace in pushGraphs and other references later on
  traverseChildren(newObject, (item: EditorObject) => {
    if (!item.type || !item.id) {
      return
    }

    if (
      // translate all shared positions except for the top parent itself (that's already translated)
      item.id !== newObjectId &&
      item?.x !== undefined &&
      item?.y !== undefined
    ) {
      item.x += newSharedX
      item.y += newSharedY
    }

    for (const device of devices) {
      const initialDeviceX = item[device]?.x
      const initialDeviceY = item[device]?.y
      if (initialDeviceX !== undefined && initialDeviceY !== undefined) {
        // @ts-ignore
        item[device].x += newSharedX
        // @ts-ignore
        item[device].y += newSharedY
      }
    }

    let newId = idMap[item.id]

    if (!newId) {
      newId = getId() as string
      idMap[item.id] = newId
    }

    item.id = newId
  })

  // replace all ids from map
  newObject = replaceAllOccurrencesFromMap(newObject, idMap)

  const builder = ObjectBuilder.merge(newObject.id, newObject.type, newObject)
    .withPosition(newObject.x, newObject.y)
    .withSize(newObject.width, newObject.height)

  const newLayoutSection = builder.build()

  return newLayoutSection
}

export default createLayoutSectionFromPrebuilt
